import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import arrow from '../../../../../assets/images/icons/arrow/chevron-right.svg';

export const useStyles = makeStyles(({
  breakpoints,
  fontFamilies,
  fontSizes,
  lineHeights,
  fontWeights,
  palette,
  siteMinWidth,
}: Theme) => ({
  root: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column-reverse',
    width: '100%',
    minWidth: siteMinWidth,
    height: 'calc(100vh - 5rem)',
    '@media (orientation: landscape)': {
      height: 'unset',
    },
    [breakpoints.up('md')]: {
      '@media (orientation: landscape)': {
        height: 'calc(100vh - 5rem)',
      },
    },
    [breakpoints.up('xl')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  services: {
    position: 'relative',
    marginTop: '-1rem',
    zIndex: 1,
    [breakpoints.up('xl')]: {
      width: '42%',
      marginTop: 0,
      padding: '4rem 2.5rem',
    },
  },
  map: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    marginBottom: 0,
    [breakpoints.up('xl')]: {
      width: '58%',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
  },
  title: {
    position: 'relative',
    padding: '1rem 1rem 2rem',
    backgroundColor: palette.primary.dark,
    color: palette.common.white,
    fontFamily: fontFamilies.primary,
    fontWeight: fontWeights.medium,
    fontSize: fontSizes.lg,
    lineHeight: lineHeights.xxl,
    margin: 0,
    borderTopLeftRadius: '1rem',
    borderTopRightRadius: '1rem',
    [breakpoints.up('md')]: {
      fontSize: fontSizes.xxl,
      padding: '1.5rem 2.5rem 2.5rem',
    },
    [breakpoints.up('xl')]: {
      padding: 0,
      margin: '0 0 4rem',
      background: 'transparent',
      color: palette.common.black,
    },
  },
  button: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1.5rem 0.75rem',
    border: '1px solid transparent',
    borderRadius: '0.75rem',
    background: 'linear-gradient(white, white) padding-box, linear-gradient(103.77deg, #d4Aeea 6.79%, #cbeaf8 49.36%, #bbdae7 91.93%) border-box',
    fontSize: '1rem',
    lineHeight: 1.5,
    cursor: 'pointer',
    boxShadow: '3px 4px 13px 0 rgba(5, 41, 103, 0.1)',
  },
  buttonWithDetails: {
    flexDirection: 'column',
    alignItems: 'stretch',
    padding: '1rem 0.75rem 1rem 3.75rem',
    cursor: 'default',
    '& img': {
      position: 'absolute',
      top: '1rem',
      left: '0.75rem',
    },
  },
  loadingButton: {
    justifyContent: 'center',
    minHeight: '5.5rem',
    cursor: 'default',
  },
  cardOverlay: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    zIndex: 1,
  },
  buttonMainRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    width: '100%',
    marginBottom: '0.5rem',
    padding: '0 2rem 0.5rem 0',
    borderBottom: `1px solid ${palette.grey['400']}`,
    [breakpoints.up('md')]: {
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingTop: '0.375rem',
      paddingBottom: '1rem',
    },
  },
  labelText: {
    maxWidth: '12.5rem',
    [breakpoints.up('md')]: {
      maxWidth: 'unset',
    },
  },
  bonusLabel: {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    padding: '0.1875rem 0.25rem 0.125rem',
    borderRadius: '0.375rem',
    background: 'linear-gradient(217deg, #04b6f8 0%, #e75fd0 100%)',
    color: palette.common.white,
    fontSize: '0.6875rem',
    fontWeight: fontWeights.medium,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    [breakpoints.up('md')]: {
      position: 'static',
      padding: '0.25rem 0.5rem',
      fontSize: '0.875rem',
      lineHeight: 1.4,
    },
  },
  detailsRow: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: 'fit-content',
    padding: 0,
    border: 'none',
    background: 'transparent',
    font: 'inherit',
    cursor: 'pointer',
  },
  detailsLabel: {
    color: palette.primary.dark,
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  buttonsContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    marginTop: '-1rem',
    padding: '0.75rem 1rem 1.5rem',
    borderTopLeftRadius: '1rem',
    borderTopRightRadius: '1rem',
    background: palette.common.white,
    [breakpoints.up('md')]: {
      padding: '1.5rem 2.5rem',
    },
    [breakpoints.up('xl')]: {
      marginTop: 0,
      padding: 0,
    },
  },
  icon: {
    flexShrink: 0,
    width: '2.5rem',
    height: '2.5rem',
  },
  label: {
    position: 'absolute',
    top: '0.25rem',
    right: '0.25rem',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.5rem',
    background: 'linear-gradient(246.01deg, #04b6f8 7.61%, #e75fd0 89.62%)',
    color: palette.common.white,
    fontSize: '0.75rem',
    fontWeight: fontWeights.medium,
    [breakpoints.up('md')]: {
      right: '2.5rem',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: '0.875rem',
    },
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: '0.75rem',
    width: '1rem',
    height: '1rem',
    background: `no-repeat transparent url(${arrow}) center`,
    backgroundSize: 'contain',
  },
  arrowDetails: {
    top: '31%',
  },
}));
